import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { isLikelyWebView, isNativeSanitarioApp, isStandaloneDisplay } from "../../lib/sanitario/bridge";

export const SANITARIO_INSTALL_DISMISSED_KEY = "sanitario-install-dismissed";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

function isIosSafari(ua) {
  return /(iPhone|iPad|iPod)/.test(ua) && /Safari\//.test(ua) && !/(CriOS|FxiOS|EdgiOS)/.test(ua);
}

/** Invito a installare Sanità sulla schermata Home. Mai nell'app nativa o se è già installata. */
export default function SanitarioInstallPrompt({ enabled = true }) {
  const [deferred, setDeferred] = useState(null);
  const [iosHint, setIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (isNativeSanitarioApp(ua) || isLikelyWebView(ua) || isStandaloneDisplay()) return undefined;
    const dismissed = parseInt(localStorage.getItem(SANITARIO_INSTALL_DISMISSED_KEY) || "0", 10);
    if (dismissed && Date.now() - dismissed < SNOOZE_MS) return undefined;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    if (isIosSafari(ua)) {
      setIosHint(true);
      setVisible(true);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible || !enabled) return null;

  const dismiss = () => {
    localStorage.setItem(SANITARIO_INSTALL_DISMISSED_KEY, String(Date.now()));
    setVisible(false);
  };
  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      setDeferred(null);
      setVisible(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-labelledby="sanitario-install-title"
      data-testid="sanitario-install-prompt"
      className="fixed inset-x-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-50 mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-4 text-slate-900 shadow-2xl md:bottom-[calc(0.75rem+env(safe-area-inset-bottom))]"
    >
      <div className="flex items-start gap-3">
        <img src="/sanita/icons/sanita-192.png" alt="" className="h-12 w-12 shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p id="sanitario-install-title" className="text-base font-semibold">Installa Sanità</p>
          {iosHint && !deferred ? (
            <p className="mt-1 text-sm text-slate-600">
              Tocca <Share className="inline h-4 w-4 align-text-bottom" aria-label="Condividi" /> e poi
              <strong> Aggiungi a Home</strong> per usare la scheda a schermo intero sul tablet.
            </p>
          ) : (
            <p className="mt-1 text-sm text-slate-600">Apri la scheda ospite dalla schermata Home, a schermo intero.</p>
          )}
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Chiudi"
          className="-m-1 inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-500"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={dismiss} className="h-11 flex-1 rounded-xl border border-slate-200 text-base font-medium">
          Non ora
        </button>
        {deferred ? (
          <button
            type="button"
            onClick={install}
            data-testid="sanitario-install-button"
            className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-[#901030] text-base font-semibold text-white"
          >
            <Download className="h-5 w-5" /> Installa
          </button>
        ) : null}
      </div>
    </div>
  );
}
